import { useState, useEffect, useCallback } from 'react';
import { postUserTestAnswer, testAnswerCancel } from '../assets/api/userActions';

export const useTestQuestionAnswer = ({
    questionId,
    attemptID,
    examDataAttempt,
    userAnswered,
    answerResHandler,
    sheetMode = 'active',
}) => {
    const [activeBtn, setActiveBtn] = useState(0);
    const [createdAnswerID, setCreatedAnswerID] = useState(0);
    const [pendingClearOptionId, setPendingClearOptionId] = useState(null);
    const [isClearing, setIsClearing] = useState(false);

    const syncFromAttempt = useCallback((qid) => {
        let found = false;
        const answers = examDataAttempt?.answers;
        if (!answers?.length) {
            setActiveBtn(0);
            setCreatedAnswerID(0);
            setPendingClearOptionId(null);
            return;
        }
        for (let i = 0; i < answers.length; i++) {
            if (qid === answers[i].question_id) {
                setActiveBtn(answers[i].option_id);
                setCreatedAnswerID(answers[i]?.id || 0);
                found = true;
                break;
            }
        }
        if (!found) {
            setActiveBtn(0);
            setCreatedAnswerID(0);
            setPendingClearOptionId(null);
        }
    }, [examDataAttempt?.answers]);

    useEffect(() => {
        syncFromAttempt(questionId);
    }, [questionId, syncFromAttempt, userAnswered]);

    const postAnswerDataHandler = useCallback((optionId) => {
        postUserTestAnswer(questionId, attemptID, optionId).then((res) => {
            if (!res?.data) return;
            answerResHandler(res.data.attempt);
            setCreatedAnswerID(res.data.created_answer?.id || 0);
        });
    }, [questionId, attemptID, answerResHandler]);

    const cancelClearAnswer = useCallback(() => {
        if (isClearing) return;
        setPendingClearOptionId(null);
    }, [isClearing]);

    const confirmClearAnswer = useCallback(async () => {
        if (!createdAnswerID || isClearing) return;
        setIsClearing(true);
        try {
            const res = await testAnswerCancel(createdAnswerID);
            if (res?.data?.attempt) {
                answerResHandler(res.data.attempt);
            }
            setActiveBtn(0);
            setCreatedAnswerID(0);
            setPendingClearOptionId(null);
        } finally {
            setIsClearing(false);
        }
    }, [createdAnswerID, isClearing, answerResHandler]);

    const onOptionClick = useCallback((optionId) => {
        if (sheetMode === 'past_readonly' || sheetMode === 'past_unanswered' || sheetMode === 'future') {
            return;
        }
        if (optionId === activeBtn) {
            // Ask for confirmation before clearing a selected answer.
            setPendingClearOptionId((current) => (current === optionId ? null : optionId));
            return;
        }
        setPendingClearOptionId(null);
        setActiveBtn(optionId);
        postAnswerDataHandler(optionId);
    }, [sheetMode, activeBtn, postAnswerDataHandler]);

    const optionsLocked = sheetMode !== 'active';

    return {
        activeBtn,
        onOptionClick,
        optionsLocked,
        pendingClearOptionId,
        confirmClearAnswer,
        cancelClearAnswer,
        isClearing,
    };
};
